import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import "./Navbar.css";

const chaptersData = [
  {
    title: "Early Life & Roots",
    period: "1891–1912",
    path: "/dr-ambedkar-early-life-education",
    desc: "From Mhow roots to Elphinstone College graduation",
    icon: "🌱",
  },
  {
    title: "International Education",
    period: "1913–1923",
    path: "/dr-ambedkar-international-education-columbia-lse",
    desc: "Columbia University, LSE, and Gray's Inn Barrister",
    icon: "🎓",
  },
  {
    title: "Social Reform & Activism",
    period: "1924–1956",
    path: "/dr-ambedkar-social-reform-movement",
    desc: "Bahishkrit Hitakarini Sabha & mass emancipation",
    icon: "✊",
  },
  {
    title: "Mahad Satyagraha",
    period: "1927",
    path: "/mahad-satyagraha-dr-ambedkar-water-rights",
    desc: "Historic water rights assertion at Chavdar Lake",
    icon: "💧",
  },
  {
    title: "Burning of Manusmriti",
    period: "1927",
    path: "/burning-manusmriti-dr-ambedkar-social-revolution",
    desc: "Decisive revolt against ancient scriptural tyranny",
    icon: "🔥",
  },
  {
    title: "Political Statesmanship",
    period: "1936–1956",
    path: "/dr-ambedkar-political-career-leadership",
    desc: "Independent Labour Party, Viceroy Council & Law Minister",
    icon: "🏛️",
  },
];

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [chaptersOpen, setChaptersOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [readingProgress, setReadingProgress] = useState(0);

  const location = useLocation();
  const islandRef = useRef(null);
  const chaptersRef = useRef(null);
  const closeTimeoutRef = useRef(null);
  const { theme, toggleTheme } = useTheme();

  const handleChaptersEnter = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    setChaptersOpen(true);
  };

  const handleChaptersLeave = () => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }
    closeTimeoutRef.current = setTimeout(() => {
      setChaptersOpen(false);
    }, 250);
  };

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  // Scroll tracking for capsule elevation & reading progress
  useEffect(() => {
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          setIsScrolled(scrollY > 24);

          const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
          if (totalHeight > 0) {
            const progress = Math.min(100, Math.max(0, (scrollY / totalHeight) * 100));
            setReadingProgress(progress);
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close overlays on route change
  useEffect(() => {
    setMobileOpen(false);
    setChaptersOpen(false);
  }, [location.pathname]);

  // Lock scroll when mobile menu is open
  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [mobileOpen]);

  // Outside click & escape listener
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        setChaptersOpen(false);
        setMobileOpen(false);
      }
    };

    const handleClickOutside = (e) => {
      if (chaptersRef.current && !chaptersRef.current.contains(e.target)) {
        setChaptersOpen(false);
      }
      if (mobileOpen && islandRef.current && !islandRef.current.contains(e.target)) {
        setMobileOpen(false);
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [mobileOpen]);

  const isCurrent = (path) => location.pathname === path;
  const isAnyChapterActive = chaptersData.some((c) => location.pathname === c.path);

  return (
    <>
      {mobileOpen && (
        <div
          className="capsule-backdrop"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Floating Island Navigation Dock */}
      <div className="floating-island-container">
        <header
          className={`floating-capsule ${isScrolled ? "capsule-scrolled" : ""}`}
          ref={islandRef}
        >
          {/* Brand Seal */}
          <Link
            to="/"
            className="capsule-brand"
            onClick={() => setMobileOpen(false)}
            aria-label="Dr. Babasaheb Ambedkar Historical Archive"
          >
            <div className="capsule-medallion">
              <img
                src="https://upload.wikimedia.org/wikipedia/commons/c/c3/Dr._Bhimrao_Ambedkar.jpg"
                alt="Dr. Babasaheb Ambedkar"
                className="medallion-photo"
                width="34"
                height="34"
              />
            </div>
            <div className="capsule-brand-text">
              <span className="capsule-brand-title">Ambedkar</span>
              <span className="capsule-brand-sub">Archive</span>
            </div>
          </Link>

          {/* Center Segmented Dock Links (Desktop) */}
          <nav className="capsule-nav" aria-label="Main Navigation">
            <div className="segmented-dock">
              <Link
                to="/"
                className={`dock-link ${isCurrent("/") ? "active-dock" : ""}`}
              >
                <span>Home</span>
              </Link>

              <Link
                to="/dr-babasaheb-ambedkar-timeline"
                className={`dock-link ${isCurrent("/dr-babasaheb-ambedkar-timeline") ? "active-dock" : ""}`}
              >
                <span>Timeline</span>
              </Link>

              {/* Chapters Popover Trigger */}
              <div
                className="chapters-popover-wrapper"
                ref={chaptersRef}
                onMouseEnter={handleChaptersEnter}
                onMouseLeave={handleChaptersLeave}
              >
                <button
                  type="button"
                  className={`dock-link popover-trigger ${isAnyChapterActive ? "active-dock" : ""}`}
                  onClick={() => setChaptersOpen(!chaptersOpen)}
                  aria-expanded={chaptersOpen}
                  aria-haspopup="true"
                >
                  <span>Chapters</span>
                  <svg
                    className={`trigger-arrow ${chaptersOpen ? "open" : ""}`}
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <polyline points="6 9 12 15 18 9" />
                  </svg>
                </button>

                {/* Floating Chapters Dropdown Card */}
                <div
                  className={`floating-chapters-card ${chaptersOpen ? "card-shown" : ""}`}
                  onMouseEnter={handleChaptersEnter}
                  onMouseLeave={handleChaptersLeave}
                >
                  <div className="chapters-card-header">
                    <span className="card-header-label">Historical Chapters</span>
                    <span className="card-header-badge">1891–1956</span>
                  </div>

                  <div className="chapters-card-grid">
                    {chaptersData.map((ch) => {
                      const active = isCurrent(ch.path);
                      return (
                        <Link
                          key={ch.path}
                          to={ch.path}
                          className={`card-chapter-row ${active ? "active-row" : ""}`}
                          onClick={() => setChaptersOpen(false)}
                        >
                          <span className="row-icon" aria-hidden="true">{ch.icon}</span>
                          <div className="row-info">
                            <div className="row-heading">
                              <span className="row-title">{ch.title}</span>
                              <span className="row-period">{ch.period}</span>
                            </div>
                            <span className="row-desc">{ch.desc}</span>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                </div>
              </div>

              <Link
                to="/dr-ambedkar-constitution-architect-contributions"
                className={`dock-link ${isCurrent("/dr-ambedkar-constitution-architect-contributions") ? "active-dock" : ""}`}
              >
                <span>Constitution</span>
              </Link>

              <Link
                to="/dr-ambedkar-philosophy-teachings-buddhism"
                className={`dock-link ${isCurrent("/dr-ambedkar-philosophy-teachings-buddhism") ? "active-dock" : ""}`}
              >
                <span>Philosophy</span>
              </Link>
            </div>
          </nav>

          {/* Right Capsule Controls */}
          <div className="capsule-actions">
            {/* Theme Toggle Button */}
            <button
              type="button"
              className="capsule-theme-btn"
              onClick={toggleTheme}
              aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
            >
              <div className={`theme-toggle-disc ${theme === "dark" ? "is-dark" : "is-light"}`}>
                {/* Sun SVG */}
                <svg
                  className="theme-sun-svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="5" />
                  <line x1="12" y1="1" x2="12" y2="3" />
                  <line x1="12" y1="21" x2="12" y2="23" />
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                  <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                  <line x1="1" y1="12" x2="3" y2="12" />
                  <line x1="21" y1="12" x2="23" y2="12" />
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                  <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                </svg>

                {/* Moon SVG */}
                <svg
                  className="theme-moon-svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                </svg>
              </div>
            </button>

            {/* Mobile Hamburger Button */}
            <button
              type="button"
              className={`capsule-burger-btn ${mobileOpen ? "open" : ""}`}
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label={mobileOpen ? "Close menu" : "Open menu"}
              aria-expanded={mobileOpen}
            >
              <span className="burger-strip strip-1" />
              <span className="burger-strip strip-2" />
            </button>
          </div>

          {/* Bottom Hairline Reading Indicator */}
          <div className="capsule-progress-track" aria-hidden="true">
            <div
              className="capsule-progress-bar"
              style={{ width: `${readingProgress}%` }}
            />
          </div>
        </header>

        {/* Mobile Frosted Floating Menu Panel */}
        <div className={`mobile-floating-menu ${mobileOpen ? "menu-open" : ""}`} aria-hidden={!mobileOpen}>
          <div className="mobile-menu-header">
            <div className="mobile-brand-wrapper">
              <div className="capsule-medallion small">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/c/c3/Dr._Bhimrao_Ambedkar.jpg"
                  alt="Dr. Babasaheb Ambedkar"
                  width="28"
                  height="28"
                />
              </div>
              <span className="mobile-brand-title">Dr. Babasaheb Ambedkar</span>
            </div>
            <button
              type="button"
              className="mobile-close-icon"
              onClick={() => setMobileOpen(false)}
              aria-label="Close menu"
            >
              ✕
            </button>
          </div>

          <div className="mobile-menu-body">
            <div className="mobile-section-tag">Navigation</div>
            <ul className="mobile-pill-list">
              <li>
                <Link
                  to="/"
                  className={`mobile-pill-link ${isCurrent("/") ? "pill-active" : ""}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span>Home</span>
                  <span className="pill-arrow">→</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/dr-babasaheb-ambedkar-timeline"
                  className={`mobile-pill-link ${isCurrent("/dr-babasaheb-ambedkar-timeline") ? "pill-active" : ""}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span>Life Timeline</span>
                  <span className="pill-arrow">→</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/dr-ambedkar-constitution-architect-contributions"
                  className={`mobile-pill-link ${isCurrent("/dr-ambedkar-constitution-architect-contributions") ? "pill-active" : ""}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span>Constitution Architecture</span>
                  <span className="pill-arrow">→</span>
                </Link>
              </li>
              <li>
                <Link
                  to="/dr-ambedkar-philosophy-teachings-buddhism"
                  className={`mobile-pill-link ${isCurrent("/dr-ambedkar-philosophy-teachings-buddhism") ? "pill-active" : ""}`}
                  onClick={() => setMobileOpen(false)}
                >
                  <span>Philosophy & Teachings</span>
                  <span className="pill-arrow">→</span>
                </Link>
              </li>
            </ul>

            <div className="mobile-section-tag" style={{ marginTop: "1.25rem" }}>
              Historical Chapters
            </div>
            <ul className="mobile-pill-list">
              {chaptersData.map((ch) => {
                const active = isCurrent(ch.path);
                return (
                  <li key={ch.path}>
                    <Link
                      to={ch.path}
                      className={`mobile-pill-link chapter-pill ${active ? "pill-active" : ""}`}
                      onClick={() => setMobileOpen(false)}
                    >
                      <div className="mobile-row-left">
                        <span className="mobile-row-icon">{ch.icon}</span>
                        <div>
                          <span className="mobile-row-title">{ch.title}</span>
                          <span className="mobile-row-period">{ch.period}</span>
                        </div>
                      </div>
                      <span className="pill-arrow">→</span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>

          <div className="mobile-menu-footer">
            <span className="mobile-motto-text">"Educate • Agitate • Organize"</span>
          </div>
        </div>
      </div>
    </>
  );
};

export default Navbar;
